import { Link } from 'react-router';
import { gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { EmptyState } from '../components/EmptyState';
import { Icon, type IconName } from '../components/Icon';
import { useUrlClear, useUrlNumber, useUrlState } from '../lib/useUrlState';
import { timeAgo } from '../lib/agentCommands';
import { formatDateTime } from '../lib/format';

/**
 * One Activity trail — phase 6 §4. The place you go when you do NOT yet know
 * which log to open: agent commands, access decisions, scheduler runs and Atlas
 * reconciles as one reverse-chronological thread.
 *
 * It does not replace the specialised views — every row links back to the one it
 * came from. It is a read: no table records "activity", the API unions the four
 * that already exist.
 *
 * Not here, on purpose: the VPN connection audit (root-only, and folding it in
 * would leak its gate) and secret values (never in this app).
 */

const ACTIVITY = gql`
  query Activity(
    $limit: Int
    $offset: Int
    $source: String
    $actorKind: String
    $actor: String
  ) {
    activity(
      limit: $limit
      offset: $offset
      source: $source
      actorKind: $actorKind
      actor: $actor
    ) {
      id
      at
      source
      actor
      actorKind
      action
      resource
      link
      outcome
      detail
    }
  }
`;

interface Event {
  id: string;
  at: string;
  source: string;
  actor: string;
  actorKind: string;
  action: string;
  resource?: string;
  link?: string;
  outcome?: string;
  detail?: string;
}

const PAGE_SIZE = 50;

/** Source → the page it came from, so the label is the vocabulary of that page. */
const SOURCE: Record<string, { label: string; icon: IconName }> = {
  access: { label: 'Access request', icon: 'access' },
  agent: { label: 'Agent command', icon: 'run' },
  scheduler: { label: 'Scheduler', icon: 'clock' },
  atlas: { label: 'Atlas whitelist', icon: 'shield' },
};

/**
 * `unknown` is its own bucket and reads as a gap, not as a human. A `job_run`
 * triggered by hand records THAT a person did it and never WHO — putting that in
 * the same bucket as an attributed row is exactly the confusion an activity trail
 * is supposed to remove.
 */
const ACTOR_KIND: Record<string, string> = {
  human: 'Person',
  agent: 'Agent',
  schedule: 'Schedule',
  unknown: 'Unattributed',
};

/** Terminal-good green, failure red, everything mid-flight stays neutral. */
function outcomeClass(o?: string): string {
  if (!o) return '';
  if (['ok', 'succeeded', 'success', 'approved', 'completed'].includes(o))
    return 'confirmed';
  if (['failed', 'error', 'denied', 'rejected'].includes(o)) return 'failed';
  if (['partial', 'pending', 'queued', 'running', 'in_progress'].includes(o))
    return 'pending';
  return '';
}

export function ActivityPage() {
  const [source, setSource] = useUrlState('source');
  const [kind, setKind] = useUrlState('kind');
  const [actor, setActor] = useUrlState('actor');
  const [page, setPage] = useUrlNumber('page', 1);
  const clearFilters = useUrlClear(['source', 'kind', 'actor']);

  const { data, loading, error, refetch } = useQuery<{ activity: Event[] }>(
    ACTIVITY,
    {
      variables: {
        limit: PAGE_SIZE,
        offset: (page - 1) * PAGE_SIZE,
        source: source || null,
        actorKind: kind || null,
        actor: actor.trim() || null,
      },
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
    },
  );

  const rows = data?.activity ?? [];
  const anyFilter = Boolean(source || kind || actor.trim());

  return (
    <div className="page page--wide operations-page activity-page">
      <header className="operations-heading">
        <div>
          <h1>Activity</h1>
          <p>
            Recent agent commands, access decisions, scheduler runs, and Atlas
            updates. Newest first.
          </p>
        </div>
        <button
          className="btn"
          disabled={loading}
          onClick={() => void refetch().catch(() => undefined)}
        >
          {loading ? 'Refreshing…' : 'Refresh'}
        </button>
      </header>
      {error && (
        <div className="operations-error" role="alert">
          <p>
            Couldn’t refresh activity. Try again or open the{' '}
            <Link to="/agents/commands">command log</Link>.
          </p>
          <button
            className="mini-btn"
            onClick={() => void refetch().catch(() => undefined)}
          >
            Retry
          </button>
        </div>
      )}
      <section
        className="operations-panel"
        aria-label="Activity results"
        aria-busy={loading}
      >
        <div className="filter-row">
          <input
            type="search"
            aria-label="Filter by actor"
            placeholder="Search actor email or agent ID"
            value={actor}
            onChange={(e) => setActor(e.target.value)}
          />
          <select
            value={source}
            onChange={(e) => setSource(e.target.value)}
            aria-label="Filter by source"
          >
            <option value="">All sources</option>
            {Object.entries(SOURCE).map(([k, v]) => (
              <option key={k} value={k}>
                {v.label}
              </option>
            ))}
          </select>
          <select
            value={kind}
            onChange={(e) => setKind(e.target.value)}
            aria-label="Filter by actor type"
          >
            <option value="">All actor types</option>
            {Object.entries(ACTOR_KIND).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
          {anyFilter && (
            <button className="mini-btn" onClick={clearFilters}>
              Clear filters
            </button>
          )}
        </div>

        {loading && !data ? (
          <div className="skeleton-row-group">
            {[0, 1, 2, 3, 4].map((i) => (
              <div className="skeleton-row" key={i} />
            ))}
          </div>
        ) : error && !data ? null : rows.length === 0 ? (
          <EmptyState
            icon="clock"
            title={
              page > 1
                ? 'No more activity'
                : anyFilter
                  ? 'Nothing matches these filters'
                  : 'No activity recorded yet'
            }
            body={
              page > 1
                ? 'Return to the previous page to see earlier results.'
                : anyFilter
                  ? 'Try a wider range — the actor filter matches part of an email or an agent id.'
                  : 'Rows appear as scheduler jobs run, access requests move, and operators issue agent commands.'
            }
          />
        ) : (
          <>
            <div
              className="operations-table-scroll"
              tabIndex={0}
              role="region"
              aria-label="Activity table"
            >
              <table className="table-dense activity-table">
                <thead>
                  {/* No SortHeader: this is a reverse-chronological THREAD, and the
                  server pages it newest-first. Re-sorting the 50 rows in hand
                  would silently reorder a window, not the trail. */}
                  <tr>
                    <th>Event</th>
                    <th>Actor</th>
                    <th>Time</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((e) => {
                    const src = SOURCE[e.source] ?? {
                      label: e.source,
                      icon: 'inbox' as IconName,
                    };
                    return (
                      <tr key={e.id}>
                        <td>
                          <div className="activity-event-title">
                            <Icon name={src.icon} />
                            <span>{e.action}</span>
                            {e.outcome && (
                              <span
                                className={`activity-outcome ${outcomeClass(e.outcome)}`}
                              >
                                {e.outcome}
                              </span>
                            )}
                          </div>
                          <div className="activity-event-resource">
                            {e.link ? (
                              <Link to={e.link}>{e.resource || src.label}</Link>
                            ) : (
                              <span>{e.resource || src.label}</span>
                            )}
                            {e.resource && (
                              <span className="muted"> · {src.label}</span>
                            )}
                          </div>
                          {e.detail && (
                            <div className="muted activity-event-detail">
                              {e.detail}
                            </div>
                          )}
                        </td>
                        <td>
                          <div className="operations-truncate" title={e.actor}>
                            {e.actor}
                          </div>
                          <div className="muted text-2xs">
                            {ACTOR_KIND[e.actorKind] ?? e.actorKind}
                          </div>
                        </td>
                        <td className="text-xs" title={formatDateTime(e.at)}>
                          {timeAgo(e.at)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
        {(rows.length > 0 || page > 1) && (
          <div className="pager">
            <span className="muted">
              {rows.length
                ? `Showing ${(page - 1) * PAGE_SIZE + 1}–${(page - 1) * PAGE_SIZE + rows.length}`
                : 'End of activity'}
              {rows.length > 0 && rows.length < PAGE_SIZE ? ' (end)' : ''}
            </span>
            <div className="pager-btns">
              <button
                className="mini-btn"
                disabled={page <= 1 || loading}
                onClick={() => setPage(page - 1)}
              >
                ‹ Prev
              </button>
              <span className="muted">Page {page}</span>
              <button
                className="mini-btn"
                disabled={rows.length < PAGE_SIZE || loading}
                onClick={() => setPage(page + 1)}
              >
                Next ›
              </button>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
